import { devLog, devError } from '../utils/devLogger';
import FCMService from './fcmService';
import { db } from '../firebase';
import { collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc, serverTimestamp, writeBatch, getDocs, getDoc, limit } from 'firebase/firestore';

export const NotificationService = {
  notifications: [],
  listeners: [],
  fcmInitialized: false,
  firestoreUnsubscribe: null,
  _visibilityHandler: null,
  _currentUserId: null,

  addListener(callback) {
    this.listeners.push(callback);
  },

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  },

  // Initialize FCM service
  async initializeFCM(currentUser) {
    if (this.fcmInitialized || !currentUser) return;
    
    try {
      // Request FCM permission and get token
      const token = await FCMService.requestPermission();
      
      if (token) {
        devLog('FCM initialized successfully');
        
        // Set up message listener for foreground notifications
        FCMService.setupMessageListener((payload) => {
          const { notification, data } = payload;
          
          if (notification) {
            this.notify(
              data?.type || 'fcm-message',
              notification.title,
              notification.body,
              data
            );
          }
        });
        
        // Subscribe to user-specific topics
        await this.subscribeToUserTopics(currentUser);
        
        this.fcmInitialized = true;
      }
    } catch (error) {
      devError('Error initializing FCM:', error);
    }
  },

  // Subscribe to relevant topics based on user role
  async subscribeToUserTopics(user) {
    if (!user) return;
    
    try {
      // Subscribe to general announcements
      await FCMService.subscribeToTopic('announcements');
      
      // Subscribe to role-specific topics
      await FCMService.subscribeToTopic(`role-${user.role}`);
      
      // Subscribe to cohort-specific topics if applicable
      if (user.cohortId) {
        await FCMService.subscribeToTopic(`cohort-${user.cohortId}`);
      }
      
      // Subscribe to user-specific topic
      await FCMService.subscribeToTopic(`user-${user.uid}`);
      
      devLog('Subscribed to FCM topics for user:', user.email);
    } catch (error) {
      devError('Error subscribing to FCM topics:', error);
    }
  },

  // Enhanced notify method with Firebase persistence and FCM support
  async notify(type, title, message, userId = null, data = {}) {
    if (userId) {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const status = String(userData.status || '').toLowerCase();
          const isInactive = userData.isActive === false ||
            ['inactive', 'dropoff', 'dropped'].includes(status);
          if (isInactive) {
            return { skipped: true, reason: 'inactive' };
          }
        }
      } catch (error) {
        devLog('NotificationService: failed to resolve user status', error);
      }
    }
    const notification = {
      type,
      title,
      message,
      userId,
      read: false,
      priority: data.priority || 'medium',
      category: data.category || 'general',
      actionUrl: data.actionUrl || null,
      createdAt: serverTimestamp(),
      data: data.additionalData || {}
    };

    try {
      // Save to Firebase notifications collection
      const docRef = await addDoc(collection(db, 'notifications'), notification);
      
      // Add ID to local notification
      const localNotification = {
        ...notification,
        id: docRef.id,
        timestamp: new Date().toISOString(),
        createdAt: new Date()
      };

      // Update local cache
      this.notifications.unshift(localNotification);
      this.listeners.forEach(listener => listener(localNotification));

      // Show browser notification for immediate feedback
      if ('Notification' in window && Notification.permission === 'granted') {
        const browserNotification = new Notification(title, {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          data: data
        });
        
        // Auto-dismiss after 5 seconds
        setTimeout(() => browserNotification.close(), 5000);
        
        // Handle notification click
        browserNotification.onclick = () => {
          window.focus();
          browserNotification.close();
          
          // Handle navigation based on notification type
          this.handleNotificationAction(type, data);
        };
      }

      return localNotification;
    } catch (error) {
      devError('Error saving notification to Firebase:', error);
      
      // Fallback to in-memory only
      const fallbackNotification = {
        id: Date.now().toString(),
        type,
        title,
        message,
        userId,
        timestamp: new Date().toISOString(),
        read: false,
        data
      };
      
      this.notifications.unshift(fallbackNotification);
      this.listeners.forEach(listener => listener(fallbackNotification));
      
      return fallbackNotification;
    }
  },
  
  // Handle notification click actions
  handleNotificationAction(type, data) {
    switch (type) {
      case 'new-message':
        if (data.conversationId) {
          // Trigger navigation to messages (would need to be implemented with router)
          devLog('Navigate to conversation:', data.conversationId);
        }
        break;
      
      case 'issue-created':
      case 'issue-assigned':
      case 'issue-status-changed':
        if (data.issue) {
          devLog('Navigate to issue:', data.issue.id);
        }
        break;
      
      case 'student-registered':
      case 'student-status-changed':
        devLog('Navigate to user management');
        break;
      
      case 'attendance-marked':
      case 'low-attendance':
        devLog('Navigate to attendance');
        break;
      
      default:
        devLog('Navigate to dashboard');
        break;
    }
  },

  async markAsRead(notificationId) {
    try {
      // Update in Firebase
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
      
      // Update local cache
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
        notification.readAt = new Date();
      }
    } catch (error) {
      devError('Error marking notification as read:', error);
      
      // Fallback to local update only
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
      }
    }
  },

  getUnreadCount() {
    return this.notifications.filter(n => !n.read).length;
  },

  getAllNotifications() {
    return this.notifications;
  },

  // Initialize real-time notification listener for user
  async initializeNotificationListener(userId) {
    if (!userId) return;

    try {
      // Clean up existing listener and visibility handler
      if (this.firestoreUnsubscribe) {
        this.firestoreUnsubscribe();
        this.firestoreUnsubscribe = null;
      }
      if (this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler);
        this._visibilityHandler = null;
      }

      this._currentUserId = userId;

      const startListener = () => {
        const q = query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(50)
        );

        this.firestoreUnsubscribe = onSnapshot(q, (snapshot) => {
          const firestoreNotifications = [];
          snapshot.forEach((d) => {
            firestoreNotifications.push({
              id: d.id,
              ...d.data(),
              timestamp: d.data().createdAt?.toDate()?.toISOString() || new Date().toISOString()
            });
          });
          this.notifications = firestoreNotifications;
          this.listeners.forEach(listener => {
            firestoreNotifications.forEach(notification => {
              if (!notification.read) listener(notification);
            });
          });
        });
      };

      startListener();

      // Pause listener when tab is hidden to avoid stale connections
      this._visibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          if (this.firestoreUnsubscribe) {
            this.firestoreUnsubscribe();
            this.firestoreUnsubscribe = null;
          }
        } else if (document.visibilityState === 'visible' && !this.firestoreUnsubscribe) {
          startListener();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);

    } catch (error) {
      devError('Error setting up notification listener:', error);
    }
  },

  // Load user notifications from Firebase
  async loadUserNotifications(userId, limitCount = 20) {
    if (!userId) return [];
    
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const notifications = [];
      
      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().createdAt?.toDate()?.toISOString() || new Date().toISOString()
        });
      });
      
      // Update local cache
      this.notifications = notifications;
      
      return notifications;
    } catch (error) {
      devError('Error loading notifications:', error);
      return [];
    }
  },

  // Bulk mark notifications as read
  async markAllAsRead(userId) {
    if (!userId) return;
    
    try {
      const batch = writeBatch(db);
      const unreadNotifications = this.notifications.filter(n => !n.read && n.userId === userId);
      
      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, 'notifications', notification.id);
        batch.update(notificationRef, {
          read: true,
          readAt: serverTimestamp()
        });
        
        // Update local cache
        notification.read = true;
        notification.readAt = new Date();
      });
      
      await batch.commit();
      
    } catch (error) {
      devError('Error marking all notifications as read:', error);
    }
  },

  // Clean up listener when user logs out
  cleanup() {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._currentUserId = null;
    this.notifications = [];
    this.fcmInitialized = false;
  },

  // Request both browser and FCM permissions
  async requestPermission(currentUser) {
    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    
    // Initialize FCM if user is provided
    if (currentUser) {
      await this.initializeFCM(currentUser);
    }
  },
  
  // Send push notification to specific users (would be called from backend)
  async sendPushNotification(userIds, title, body, data = {}) {
    // This would typically be handled by a backend service using Firebase Admin SDK
    devLog('Push notification would be sent to users:', userIds, { title, body, data });
  },

  // Send notification to topic (for group notifications)
  async sendTopicNotification(topic, title, body, data = {}) {
    // This would typically be handled by a backend service using Firebase Admin SDK
    devLog('Topic notification would be sent to:', topic, { title, body, data });
  },

  // Enhanced notification methods with Firebase persistence and FCM support
  async notifyStudentRegistered(student) {
    const notification = await this.notify(
      'student-registered',
      'New Student Registered',
      `${student.displayName} has successfully joined the LMS`,
      null, // No specific user, this goes to admins
      { 
        category: 'user_management',
        priority: 'medium',
        actionUrl: '/admin/users',
        additionalData: { student }
      }
    );
    
    // Send FCM notification to admins and facilitators
    await this.sendTopicNotification(
      'role-admin',
      'New Student Registered',
      `${student.displayName} has joined the LMS`,
      { type: 'student-registered', studentId: student.uid }
    );
    
    return notification;
  },

  async notifyStudentStatusChanged(student, oldStatus, newStatus) {
    return await this.notify(
      'student-status-changed',
      'Student Status Updated',
      `${student.displayName} status changed from ${oldStatus} to ${newStatus}`,
      null,
      { 
        category: 'user_management',
        priority: 'medium',
        additionalData: { student, oldStatus, newStatus }
      }
    );
  },

  async notifyStudentTransferred(student, fromCohort, toCohort) {
    return await this.notify(
      'student-transferred',
      'Student Transferred',
      `${student.displayName} transferred from ${fromCohort} to ${toCohort}`,
      null,
      {
        category: 'user_management',
        priority: 'medium',
        additionalData: { student, fromCohort, toCohort }
      }
    );
  },

  async notifyStudentProgramAssigned(student, programName) {
    return await this.notify(
      'student-program-assigned',
      'Program Assignment Updated',
      `${student.displayName} has been assigned to program: ${programName}`,
      student.uid,
      {
        category: 'user_management',
        priority: 'medium',
        additionalData: { student, programName }
      }
    );
  },

  async notifyIssueCreated(issue) {
    return await this.notify(
      'issue-created',
      'New Issue Reported',
      `Issue: ${issue.title} (Priority: ${issue.priority})`,
      null,
      { 
        category: 'issues',
        priority: issue.priority,
        actionUrl: `/issues/${issue.id}`,
        additionalData: { issue }
      }
    );
  },

  async notifyIssueStatusChanged(issue, oldStatus, newStatus) {
    return await this.notify(
      'issue-status-changed',
      'Issue Status Updated',
      `Issue "${issue.title}" status changed from ${oldStatus} to ${newStatus}`,
      issue.assignedTo || null,
      { 
        category: 'issues',
        priority: issue.priority,
        actionUrl: `/issues/${issue.id}`,
        additionalData: { issue, oldStatus, newStatus }
      }
    );
  },

  async notifyIssueAssigned(issue, facilitator) {
    return await this.notify(
      'issue-assigned',
      'Issue Assigned',
      `Issue "${issue.title}" assigned to ${facilitator.displayName}`,
      facilitator.uid,
      { 
        category: 'issues',
        priority: issue.priority,
        actionUrl: `/issues/${issue.id}`,
        additionalData: { issue, facilitator }
      }
    );
  },

  async notifyAttendanceMarked(session, studentCount) {
    return await this.notify(
      'attendance-marked',
      'Attendance Recorded',
      `Attendance marked for ${studentCount} students in ${session}`,
      null,
      { 
        category: 'attendance',
        priority: 'low',
        additionalData: { session, studentCount }
      }
    );
  },

  async notifyAttendanceConfirmation(learner, session) {
    return await this.notify(
      'attendance-confirmation',
      'Confirm Your Attendance',
      `Please confirm your attendance for ${session}.`,
      learner.uid,
      {
        category: 'attendance',
        priority: 'medium',
        actionUrl: '/attendance',
        additionalData: { session }
      }
    );
  },

  async notifyAttendanceRegisterSign(learner, session, sessionId, sessionDate) {
    const dateLine = sessionDate ? ` Please enter this date on the form: ${sessionDate}.` : '';
    return await this.notify(
      'attendance-register-sign',
      'Sign Attendance Register',
      `Please sign the attendance register for ${session}.${dateLine}`,
      learner.uid,
      {
        category: 'attendance',
        priority: 'medium',
        actionUrl: 'https://form.jotform.com/260286548080561',
        additionalData: { session, sessionId, sessionDate }
      }
    );
  },

  async notifyLowAttendance(student, attendanceRate) {
    return await this.notify(
      'low-attendance',
      'Low Attendance Alert',
      `${student.displayName} has low attendance: ${attendanceRate}%`,
      student.uid,
      { 
        category: 'attendance',
        priority: 'high',
        actionUrl: '/attendance',
        additionalData: { student, attendanceRate }
      }
    );
  },

  async notifyProgressReportDue(cohort, facilitator) {
    return await this.notify(
      'progress-report-due',
      'Progress Report Required',
      `Progress report due for ${cohort.name}`,
      facilitator.uid,
      { 
        category: 'reporting',
        priority: 'medium',
        actionUrl: '/facilitator/reports',
        additionalData: { cohort, facilitator }
      }
    );
  }
};

export default NotificationService;
